import { FC, memo, useContext } from "react";
import {
  Box,
  createStyles,
  makeStyles,
  useMediaQuery,
} from "@material-ui/core";
import { includes } from "lodash";

import { Button } from "components";
import { AppContext } from "contexts";
import { useTimer } from "hooks";
import { formatDateRangeCompact } from "lib/format";
import { ListingWithIncludes } from "services/listings/types";
import {
  ACTIVE_OFFER_STATES,
  FAILED_OFFER_STATES,
  NON_FAILED_DEAL_STATES,
} from "services/offers/constants";
import { ListingOffer, OfferState } from "services/offers/types";
import theme from "theme";

export const offerButtonStyle = {
  borderRadius: "3px",
  boxShadow: "none",
  fontSize: "14px",
  lineHeight: "unset",
  margin: "0 3px",
  minHeight: "unset",
  padding: "3px 4px",
  textTransform: "none" as const,
  whiteSpace: "nowrap" as const,
  [theme.breakpoints.down("xs")]: {
    fontSize: "10px",
  },
};

interface StyleProps {
  status?: string;
  active?: boolean;
  isAssetOwner?: boolean;
  isOfferOwner?: boolean;
}

const useStyles = makeStyles(() =>
  createStyles({
    timer: {
      color: theme.custom.palette.blue.main,
    },
    offerButton: offerButtonStyle,
    fontWeightMedium: {
      fontWeight: 400,
    },
    statusOfOffer: (props: StyleProps) => ({
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      color:
        props.status && includes(NON_FAILED_DEAL_STATES, props.status)
          ? theme.custom.palette.green.main
          : props.status === "awaiting_acceptance"
          ? theme.custom.palette.yellow.main
          : "inherit",
      [theme.breakpoints.down("xs")]: {
        fontSize: 10,
      },
    }),
    offerStatus: (props: StyleProps) => ({
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      height: "100%",
      justifyContent:
        (props.isAssetOwner || props.isOfferOwner) && props.active
          ? "space-between"
          : "center",
      "& label": {
        margin: theme.spacing(0.625),
      },
      "& i": {
        margin: theme.spacing(0, 0.4),
      },
      "& span:first-of-type": {
        marginRight: theme.spacing(0.5),
      },
      "& span:last-of-type": {
        fontWeight: theme.typography.fontWeightMedium,
        marginLeft: theme.spacing(0.5),
        width: 65,
      },
      [theme.breakpoints.down("xs")]: {
        "& span:first-of-type": {
          fontSize: 10,
        },
        "& span:last-of-type": {
          fontSize: 10,
          width: 42,
        },
      },
    }),
  }),
);

const offerStateToDisplayName: Record<OfferState, string> = {
  awaiting_acceptance: "Pending",
  awaiting_docs: "Accepted",
  awaiting_due_diligence: "Accepted",
  awaiting_payment: "Accepted",
  awaiting_transfer: "Accepted",
  filled: "Accepted",
  countered: "Countered",
  fail_auto_offer_upgraded: "Increased",
  fail_buy_order_canceled: "Canceled",
  fail_offer_increased: "Increased",
  fail_offer_outbid: "Outbid",
  fail_offer_rejected_by_increase: "Rejected",
  fail_counteroffer_made: "Rejected",
  fail_docs_timeout: "Failed",
  fail_due_diligence: "Failed",
  fail_due_diligence_timeout: "Failed",
  fail_kyw_offer_prioritized: "Canceled",
  fail_new_gama_execution: "Canceled",
  fail_offer_auto_rejected: "Rejected",
  fail_offer_auto_withdrawn: "Canceled",
  fail_offer_rejected: "Rejected",
  fail_offer_withdrawn: "Withdrawn",
  fail_offer_timeout: "Expired",
  fail_payment: "Failed",
  fail_payment_timeout: "Failed",
  fail_publisher_matched: "Canceled",
  fail_transfer: "Failed",
};

interface OfferStateProps {
  offerState: OfferState;
  offerStateDisplayName?: string;
  isAcceptingFinalOffers?: boolean;
}

const OfferStateIcon: FC<OfferStateProps> = memo(
  ({ offerState, offerStateDisplayName, isAcceptingFinalOffers }) => {
    const status = isAcceptingFinalOffers
      ? "awaiting_due_diligence"
      : offerState;
    const classes = useStyles({ status });

    return offerState !== "awaiting_acceptance" || isAcceptingFinalOffers ? (
      // Since we don't have a NON_FAILED_DEAL_STATES specific to AFO, I'm passing awaiting_due_diligence as the status for AFO
      <Box className={classes.statusOfOffer}>
        {offerStateDisplayName}
        {includes(ACTIVE_OFFER_STATES, offerState) && (
          <i className="fa fa-circle" />
        )}
      </Box>
    ) : null;
  },
);

interface OfferStatusProps {
  setIsAcceptOfferModalOpen: () => void;
  setIsRejectOfferModalOpen: () => void;
  setIsCounterofferModalOpen: () => void;
  setIsEditOfferModalOpen: () => void;
  setIsWithdrawOfferModalOpen: () => void;
  listing: ListingWithIncludes;
  offer: ListingOffer;
  isLiveOffer: boolean;
  isAssetOwner: boolean;
  isChainActive: boolean;
}

export const OfferStatus: FC<OfferStatusProps> = ({
  setIsAcceptOfferModalOpen,
  setIsRejectOfferModalOpen,
  setIsCounterofferModalOpen,
  setIsEditOfferModalOpen,
  setIsWithdrawOfferModalOpen,
  listing,
  offer,
  isLiveOffer,
  isAssetOwner,
  isChainActive,
}) => {
  const classes = useStyles({});
  const { user } = useContext(AppContext);

  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const currentTime = useTimer();

  const isCounteroffer = !!offer.countered_offer_id;
  const isLastCounteroffer = isCounteroffer && !offer.countered_offer_id;
  const isSellerCounteroffer = offer.kind === "seller_counteroffer";
  const isOwner = user?.userId === offer.buyer_id;
  const isFailedDealState = FAILED_OFFER_STATES.includes(offer.state);

  const {
    state,
    is_eligible_to_withdraw_offers_placed_before_update,
    valuation,
  } = listing || {};

  const isAcceptingFinalOffers = state === "accepting_final_offers";

  const counterofferDisplayName = `${
    isSellerCounteroffer ? "Seller" : "Buyer"
  } ${
    !isMobile
      ? `Counteroffer ${
          isFailedDealState ? `(${offerStateToDisplayName[offer.state]})` : ""
        }`
      : `Offer ${
          isFailedDealState ? `(${offerStateToDisplayName[offer.state]})` : ""
        }`
  }`;

  const canEditOffer = !isSellerCounteroffer && isOwner;
  const canWithdrawOffer =
    is_eligible_to_withdraw_offers_placed_before_update &&
    offer.created_at < valuation.statistics_last_updated;

  const canAcceptRejectOffer = isCounteroffer
    ? isSellerCounteroffer
      ? isOwner
      : isAssetOwner
    : isAssetOwner;

  const offerStatusClasses = useStyles({
    active: isLiveOffer,
    isAssetOwner,
    isOfferOwner: isOwner,
  });

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
        height="100%"
      >
        {!isAcceptingFinalOffers && (
          <>
            {isLiveOffer && canAcceptRejectOffer && (
              <Box display="flex">
                <Button
                  className={classes.offerButton}
                  customColor="green"
                  variant="contained"
                  onClick={() => setIsAcceptOfferModalOpen()}
                >
                  Accept
                </Button>
                <Box display="flex" alignItems="center" component="span">
                  {isChainActive && offer.state !== "countered" ? (
                    <Button
                      className={classes.offerButton}
                      customColor="yellow"
                      variant="contained"
                      onClick={() => setIsCounterofferModalOpen()}
                    >
                      Counter
                    </Button>
                  ) : (
                    <Button
                      className={classes.offerButton}
                      customColor="softRed"
                      variant="contained"
                      onClick={() => setIsRejectOfferModalOpen()}
                    >
                      Reject
                    </Button>
                  )}
                </Box>
              </Box>
            )}
            {isLiveOffer && canEditOffer && (
              <Box display="flex">
                <Button
                  className={`${classes.offerButton} ${classes.fontWeightMedium}`}
                  customColor="white"
                  variant="outlined"
                  onClick={setIsEditOfferModalOpen}
                  id="edit-your-offer"
                >
                  {isMobile ? "Increase" : "Increase Offer"}
                </Button>
                {canWithdrawOffer && (
                  <Button
                    className={`${classes.offerButton} ${classes.fontWeightMedium}`}
                    customColor="white"
                    variant="outlined"
                    onClick={setIsWithdrawOfferModalOpen}
                  >
                    Withdraw
                  </Button>
                )}
              </Box>
            )}
          </>
        )}
        <Box className={offerStatusClasses.offerStatus}>
          {!!isLiveOffer && !isAcceptingFinalOffers ? (
            <span className={classes.timer}>
              {formatDateRangeCompact(offer.expiration, currentTime)}
            </span>
          ) : !!isLiveOffer && isAcceptingFinalOffers ? (
            <Box ml={5}>
              <OfferStateIcon
                offerState={offer.state}
                offerStateDisplayName="Highest"
                isAcceptingFinalOffers
              />
            </Box>
          ) : (
            <OfferStateIcon
              offerState={offer.state}
              offerStateDisplayName={
                isCounteroffer && !isLastCounteroffer
                  ? counterofferDisplayName
                  : offerStateToDisplayName[offer.state]
              }
            />
          )}
        </Box>
      </Box>
    </>
  );
};
